import { dropTask } from 'ember-concurrency';
import { Form, type FormResultData } from 'tio-ui/components/forms';
import Modal from 'tio-reimagined/components/modal';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { type IntlService } from 'ember-intl';
import * as v from 'valibot';
import Component from '@glimmer/component';
import formatSubdomainRedirectUrl from 'tio-common/utils/format-subdomain-redirect-url';
import { parseHostname } from 'tio-common/utils/hostname';
import TioFooter from 'tio-common/components/tio/footer';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type PreRegisterService from '../../services/pre-register.ts';
import { Button } from 'tio-ui/components/buttons';
import { VStack } from 'tio-ui/components/layout';

interface EmailLookupWrapperComponentSignature {
  Args: {
    route: string;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

const formSchema = v.object({
  email: v.pipe(v.string(), v.nonEmpty('Email is required'), v.email('Please enter a valid email')),
});

type EmailLookupFormData = v.InferOutput<typeof formSchema>;

export default class EmailLookupWrapper extends Component<EmailLookupWrapperComponentSignature> {
  @service declare preRegister: PreRegisterService;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;

  @tracked formData: EmailLookupFormData = { email: '' };
  @tracked showUnregisteredModal = false;

  get showEmailLookup() {
    return this.preRegister.shouldShowEmailLookup;
  }

  handleFormChange = (result: FormResultData<EmailLookupFormData>) => {
    this.formData = result.data;
  };

  handleFormSubmit = dropTask(async (result: FormResultData<EmailLookupFormData>) => {
    const { email } = result.data;
    const { valid, sub_domain, unregistered } = await this.preRegister.emailLookup(email);

    if (unregistered) {
      this.showUnregisteredModal = true;
      return;
    }

    if (valid) {
      const redirectUrl = formatSubdomainRedirectUrl({
        subDomain: sub_domain,
        domain: parseHostname(window.location.hostname).domain,
        email: email,
        route: this.args.route,
      });
      window.location.href = redirectUrl;
    } else {
      this.notifications.add(this.intl.t('login.email_not_found'), {
        appearance: 'error',
      });
    }
  });

  closeUnregisteredModal = () => {
    this.showUnregisteredModal = false;
  };

  <template>
    <div class="flex flex-col h-screen container mx-auto pb-20 w-full overflow-auto">
      <div class="login flex flex-col gap-y-10 justify-center items-center mt-20">
        <div class="bg-white w-full max-w-xl shadow-2xl p-5">
          {{#if this.showEmailLookup}}
            <h2 class="text-lg text-center mb-4">
              {{t "login.what_email"}}
            </h2>
            <Form
              @data={{this.formData}}
              @schema={{formSchema}}
              @onChange={{this.handleFormChange}}
              @onSubmit={{this.handleFormSubmit.perform}}
              as |form|
            >
              <VStack>
                <form.Field @name="email" as |field|>
                  <field.Input @label={{t "login.email"}} @type="email" />
                </form.Field>
                <div class="flex flex-col items-center">
                  <Button
                    @type="submit"
                    @intent="primary"
                    @isRunning={{this.handleFormSubmit.isRunning}}
                    class="w-36"
                  >
                    {{t "common.submit"}}
                  </Button>
                </div>
              </VStack>
            </Form>
          {{else}}
            {{yield}}
          {{/if}}
          <div class="mt-6 text-left text-sm text-gray-600">
            {{t "login.need_help"}}
            <a
              href="https://www.tuition.io/support"
              target="_blank"
              rel="noopener noreferrer"
              class="underline text-ocean-600 hover:text-ocean-700"
            >
              {{t "login.contact_support"}}
            </a>
          </div>
        </div>
      </div>
      <Modal @isOpen={{this.showUnregisteredModal}} @onClose={{this.closeUnregisteredModal}}>
        <:header>{{t "login.unregistered_email"}}</:header>
        <:body>{{t "login.unregistered_email_body"}}</:body>
      </Modal>
      <TioFooter />
    </div>
  </template>
}
